// ─── Auth Context ───────────────────────────────────────────────
// Wraps the entire app. Uses Supabase onAuthStateChange — fires on
// sign-in, sign-out, token refresh, tab focus recovery. No polling.
//
// HARDENED: Absolute 8-second timeout on initialization to prevent
// infinite "Checking session..." hang caused by stale localStorage
// tokens, network issues, or Supabase lock contention. The timeout only
// ends the loading state; it never deletes a stored session.

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { sessionExpiredBus } from '../api';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

// ─── Types ───────────────────────────────────────────────────────

export type UserTier = 'builder' | 'developer';

export interface Subscriber {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  plan: 'indie' | 'studio' | 'team';
  billing_period: 'monthly' | 'annual';
  status: 'trialing' | 'active' | 'past_due' | 'cancelled';
  trial_started_at: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  credit_balance_remaining: number;
  token_balance_reset_at: string | null;
  created_at: string;
  updated_at: string;
  user_tier?: UserTier | null;
  product?: 'forge' | 'factory';
  hasForge?: boolean;
  hasFactory?: boolean;
}

export interface AuthState {
  user: User | null;
  session: Session | null;
  subscriber: Subscriber | null;
  /** True once the subscriber fetch has settled (found, not-found, or errored).
   *  Consumers must NOT treat `subscriber === null` as "no subscriber" until
   *  this is true — before it, null just means "still loading". */
  subscriberResolved: boolean;
  /** True when the LAST subscriber fetch ERRORED (network/RLS/authz/transient)
   *  rather than confirming the row is absent. Consumers must NOT treat the user
   *  as orphaned while this is true — a failed fetch is not a missing account. */
  subscriberFetchFailed: boolean;
  loading: boolean;
  /** True when a persistent 401 was received and session could not be refreshed */
  sessionExpired: boolean;
  signOut: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>;
  signInWithMagicLink: (email: string, captchaToken?: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  refreshSubscriber: () => Promise<void>;
  userTier: UserTier;
  setUserTier: (tier: UserTier) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// ─── Module-level session cache ─────────────────────────────────
// Exported so api.ts can read the current session WITHOUT calling
// supabase.auth.getSession() (which can hang forever due to internal
// initialization promise deadlocks). Updated by onAuthStateChange.
let _cachedSession: Session | null = null;

/** Get the current cached session — populated by AuthProvider's onAuthStateChange.
 *  Returns null if no session or AuthProvider hasn't initialized yet. */
export function getCachedSession(): Session | null {
  return _cachedSession;
}

// There must be one Supabase subscription for the portal, not one per
// AuthProvider instance. Keeping the subscription here also protects against
// accidental double-mounts (including development StrictMode remounts).
type AuthStateHandler = (event: AuthChangeEvent, session: Session | null) => void | Promise<void>;
let sharedAuthSubscription: { unsubscribe: () => void } | null = null;
let sharedAuthHandler: AuthStateHandler | null = null;
let sharedAuthConsumers = 0;

function subscribeToSharedAuth(handler: AuthStateHandler): () => void {
  sharedAuthHandler = handler;
  sharedAuthConsumers += 1;

  if (!sharedAuthSubscription) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // The callback is intentionally shared; provider-local state remains in
      // the current handler, while Supabase only sees one registration.
      void sharedAuthHandler?.(event, session);
    });
    sharedAuthSubscription = subscription;
  }

  let active = true;
  return () => {
    if (!active) return;
    active = false;
    sharedAuthConsumers -= 1;
    if (sharedAuthConsumers === 0) {
      sharedAuthSubscription?.unsubscribe();
      sharedAuthSubscription = null;
      sharedAuthHandler = null;
    }
  };
}

// ─── Constants ─────────────────────────────────────────────────

/** Maximum time (ms) to wait for Supabase getSession before giving up */
const SESSION_TIMEOUT_MS = 5_000;

/** Absolute hard timeout (ms) — if loading is still true after this, force it false */
export const HARD_TIMEOUT_MS = 8_000;
const USER_TIER_STORAGE_KEY = 'ocnexus:user_tier';

function normalizeUserTier(value: unknown): UserTier {
  return value === 'developer' ? 'developer' : 'builder';
}

function readStoredUserTier(): UserTier {
  if (typeof window === 'undefined') return 'builder';
  try {
    return normalizeUserTier(window.localStorage.getItem(USER_TIER_STORAGE_KEY));
  } catch {
    return 'builder';
  }
}

function writeStoredUserTier(tier: UserTier): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(USER_TIER_STORAGE_KEY, tier);
  } catch {
    // Ignore storage failures.
  }
}

// ─── Provider ────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [subscriber, setSubscriber] = useState<Subscriber | null>(null);
  // ocn-S567-P0: tracks whether the subscriber fetch has actually settled
  // (found, not-found, or errored). The App-shell orphaned-session guard must
  // not sign a user out while this is still false — otherwise a valid login is
  // bounced to the marketing site during the transient render between sign-in
  // (user set) and the subscriber fetch resolving.
  const [subscriberResolved, setSubscriberResolved] = useState(false);
  // ocn: distinguishes "subscriber row confirmed absent" (genuine orphan → safe to
  // sign out) from "fetch errored" (transient/RLS/network → keep the session).
  const [subscriberFetchFailed, setSubscriberFetchFailed] = useState(false);
  const [userTier, setUserTierState] = useState<UserTier>(() => readStoredUserTier());
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Keep the auth callback independent from React state changes. In particular,
  // a re-authentication clears sessionExpired while the callback is running;
  // the subscription must not be torn down and recreated as a result.
  const sessionExpiredRef = useRef(false);
  const lastSignedInSessionRef = useRef<string | null>(null);
  const loadingResolved = useRef(false);

  /** Mark loading as done — idempotent, only fires once */
  const resolveLoading = useCallback(() => {
    if (!loadingResolved.current) {
      loadingResolved.current = true;
      setLoading(false);
    }
  }, []);

  const fetchSubscriber = useCallback(async (userId: string) => {
    // ocn-S567-P0: mark the fetch in-flight (synchronously, before the first
    // await) so the orphaned-session guard sees subscriberResolved=false while
    // the row is loading and does NOT sign the just-authenticated user out.
    setSubscriberResolved(false);
    // ocn: maybeSingle() returns {data:null,error:null} for a genuinely-absent
    // row, so we can tell a REAL orphan apart from a transient/RLS/network
    // failure. .single() conflated the two — ANY blip nulled the subscriber and
    // the App orphan-guard signed a valid user out. One quick retry absorbs a
    // transient blip before we decide.
    // Product flags come from the API resolver: the Factory-is-Forge
    // containment rule must not be copied into dashboard consumers.
    const fetchOnce = async () => {
      const session = _cachedSession;
      if (!session?.access_token) return supabase.from('subscribers').select('*').eq('id', userId).maybeSingle();
      const base = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
      const response = await fetch(`${base}/api/auth/me`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (!response.ok) throw new Error(`subscriber profile request failed: ${response.status}`);
      const body = await response.json() as { subscriber: Subscriber | null };
      return { data: body.subscriber, error: null };
    };
    try {
      let { data, error } = await fetchOnce();
      if (error) {
        await new Promise((r) => setTimeout(r, 1200));
        ({ data, error } = await fetchOnce());
      }

      if (error) {
        // Transient / RLS / authz / network — NOT a missing account. Keep the
        // existing subscriber (and session) and flag the failure so the orphan
        // guard stands down.
        console.warn('[Auth] Subscriber fetch failed (keeping session):', error.message);
        setSubscriberFetchFailed(true);
        return;
      }

      setSubscriberFetchFailed(false);
      if (!data) {
        // Row confirmed absent — a genuine orphan (no subscribers row).
        setSubscriber(null);
      } else {
        const nextSubscriber = data as Subscriber;
        const nextTier = normalizeUserTier(nextSubscriber.user_tier ?? readStoredUserTier());
        writeStoredUserTier(nextTier);
        setUserTierState(nextTier);
        setSubscriber({ ...nextSubscriber, user_tier: nextTier });
      }
    } catch (err) {
      // Network throw — treat as transient, never as an orphan.
      console.warn('[Auth] Subscriber fetch error (keeping session):', err);
      setSubscriberFetchFailed(true);
    } finally {
      // Fetch settled — the orphaned-session guard may trust a null subscriber
      // as genuinely orphaned ONLY when the fetch did not fail.
      setSubscriberResolved(true);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // ── Hard timeout: absolute safety net ───────────────────────
    // If anything in Supabase hangs (lock contention, stale token
    // refresh retries, network timeout), this ensures the UI never
    // stays on "Checking session..." forever.
    //
    // DEGRADE, don't DESTROY (same rule as the getSession timeout branch
    // below): a slow init on a slow network is not evidence the session is
    // dead. This used to delete every sb-* localStorage key and null the
    // session, which logged valid users out. Now it only ends the loading
    // state; whatever state is already set stays, and onAuthStateChange
    // settles the real auth state when Supabase finishes. A stored session is
    // cleared only by an explicit getSession error or a SIGNED_OUT event.
    const hardTimer = setTimeout(() => {
      if (mounted && !loadingResolved.current) {
        console.warn(`[Auth] Hard timeout (${HARD_TIMEOUT_MS}ms) — rendering without waiting further, awaiting onAuthStateChange`);
        resolveLoading();
      }
    }, HARD_TIMEOUT_MS);

    // ── Session initialization ──────────────────────────────────
    async function initSession() {
      try {
        console.log('[Auth] Initializing session...');

        // Race getSession against a timeout.
        // getSession() internally awaits initializePromise which can hang
        // if there's a stale refresh token being retried.
        const result = await Promise.race([
          supabase.auth.getSession(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), SESSION_TIMEOUT_MS)),
        ]);

        if (!mounted) return;

        if (!result) {
          // Timeout — getSession() itself hasn't resolved yet (e.g. a stale
          // token refresh still in flight). This is NOT evidence the session
          // is dead: on a slow network the refresh frequently completes 1-2s
          // later via onAuthStateChange's SIGNED_IN. Destroying the session
          // here (localStorage wipe + signOut) races that in-flight refresh
          // and produces a visible flicker to logged-out — sometimes a full
          // relogin — even though the session was perfectly valid.
          //
          // DEGRADE, don't DESTROY: resolve loading so the app renders, and
          // leave session/user/subscriber state untouched — the existing
          // onAuthStateChange subscription (below) settles the real auth
          // state whenever the refresh actually lands. A genuinely dead/
          // no-op session is still handled correctly: an explicit no-session
          // getSession() result (s === null, not this timeout branch) already
          // clears user/session naturally below, and a real auth error or
          // SIGNED_OUT event still hard-clears via their own branches. The
          // HARD_TIMEOUT_MS failsafe follows the same rule and never clears.
          console.warn(`[Auth] getSession timed out after ${SESSION_TIMEOUT_MS}ms — rendering optimistically, awaiting onAuthStateChange`);
          resolveLoading();
          return;
        }

        const { data: { session: s }, error } = result;

        if (error) {
          console.warn('[Auth] getSession error:', error.message);
          // Clear corrupt session
          supabase.auth.signOut().catch(() => {});
          _cachedSession = null;
          setSession(null);
          setUser(null);
          setSubscriber(null);
          resolveLoading();
          return;
        }

        console.log('[Auth] Session:', s ? `user=${s.user.email}` : 'none');
        _cachedSession = s;
        setSession(s);
        setUser(s?.user ?? null);

        if (s?.user) {
          await fetchSubscriber(s.user.id);
        }
      } catch (err) {
        console.warn('[Auth] Init error:', err);
        setSession(null);
        setUser(null);
        setSubscriber(null);
      } finally {
        if (mounted) {
          resolveLoading();
        }
      }
    }

    void initSession();

    // Listen for auth changes (sign-in, sign-out, token refresh). The shared
    // manager guarantees one underlying Supabase subscription even if the
    // provider is mounted more than once.
    const unsubscribeAuth = subscribeToSharedAuth(
      async (_event, s) => {
        if (!mounted) return;

        // Supabase can replay SIGNED_IN while initialization or a refresh is
        // settling. Treat an identical replay as a no-op so it cannot trigger
        // another subscriber fetch (or a downstream refetch storm).
        const signedInKey = s?.user && `${s.user.id}:${s.access_token}`;
        if (_event === 'SIGNED_IN' && signedInKey === lastSignedInSessionRef.current) {
          return;
        }
        if (_event === 'SIGNED_IN') {
          lastSignedInSessionRef.current = signedInKey ?? null;
        } else if (_event === 'SIGNED_OUT') {
          lastSignedInSessionRef.current = null;
        }

        console.log('[Auth] State change:', _event, s?.user?.email ?? 'no user');
        _cachedSession = s;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          // Re-authenticated — clear expired state
          if (sessionExpiredRef.current) {
            sessionExpiredRef.current = false;
            setSessionExpired(false);
            sessionExpiredBus.reset();
          }
          await fetchSubscriber(s.user.id);
        } else {
          setSubscriber(null);
        }
        resolveLoading();
      }
    );

    // ── Listen for persistent 401s from api.ts ───────────────────
    const unsubExpired = sessionExpiredBus.on(() => {
      console.warn('[Auth] Session expired (persistent 401) — clearing state');
      sessionExpiredRef.current = true;
      _cachedSession = null;
      setSession(null);
      setUser(null);
      setSubscriber(null);
      setSessionExpired(true);
    });

    // Proactive session refresh every 30 minutes to prevent token expiry
    // during idle periods (autoRefreshToken only fires on Supabase API calls)
    const refreshInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          console.warn('[Auth] Proactive refresh failed:', error.message);
        } else if (data.session) {
          _cachedSession = data.session;
          setSession(data.session);
          console.log('[Auth] Proactive refresh: token renewed');
        }
      } catch {
        // Network error — silently retry next interval
      }
    }, 10 * 60 * 1000); // 10 minutes — aggressive refresh to prevent 401s

    return () => {
      mounted = false;
      clearTimeout(hardTimer);
      clearInterval(refreshInterval);
      unsubscribeAuth();
      unsubExpired();
    };
  }, [fetchSubscriber, resolveLoading]);

  // ─── Auth Methods ──────────────────────────────────────────────

  const signOut = useCallback(async () => {
    // Clear Supabase session tokens from localStorage first
    try {
      const keys = Object.keys(localStorage);
      keys.filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
    // Clear any wizard/session state from sessionStorage
    try { sessionStorage.clear(); } catch { /* ignore */ }
    // Attempt Supabase signOut but don't let it block the redirect
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch {
      // Supabase signOut failed — proceed with redirect anyway
    }
    // Full reload to clear all state. In production land on the portal's
    // sign-in page (portal root renders Login when signed out), not the
    // marketing site. Local/dev hosts just reload.
    if (window.location.hostname.includes('ocnexus.dev')) {
      window.location.href = 'https://portal.ocnexus.dev/';
    } else {
      window.location.reload();
    }
  }, []);

  const signInWithGitHub = useCallback(async () => {
    const redirectTo = window.location.hostname.includes('ocnexus.dev')
      ? 'https://portal.ocnexus.dev/auth/callback'
      : `${window.location.origin}?page=auth-callback`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo,
        scopes: 'repo',  // Required: repo access for branches, PRs, commits
      },
    });
    if (error) throw error;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const redirectTo = window.location.hostname === 'portal.ocnexus.dev'
      ? 'https://portal.ocnexus.dev/auth/callback'
      : `${window.location.origin}?page=auth-callback`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) throw error;
  }, []);

  // ─── Email + password sign-in (Supabase-native) ───────────────

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUpWithPassword = useCallback(async (email: string, password: string) => {
    const redirectTo = window.location.hostname.includes('ocnexus.dev')
      ? 'https://portal.ocnexus.dev?page=auth-callback'
      : `${window.location.origin}?page=auth-callback`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo },
    });
    if (error) throw error;
    // Supabase anti-enumeration: signUp on an already-registered email returns
    // 200 with an obfuscated user whose identities array is empty and sends NO
    // email. Surface it so the user signs in / resets instead of waiting.
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      throw new Error('An account with this email already exists. Sign in, or use "Forgot password" to reset it.');
    }
    // needsConfirmation=true means Supabase sent a confirmation email
    const needsConfirmation = !data.session;
    return { needsConfirmation };
  }, []);

  // ─── Magic-link (Mailgun-based via API) ───────────────────────

  const signInWithMagicLink = useCallback(async (email: string, captchaToken?: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const res = await fetch(`${apiUrl}/api/auth/magic-link/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, captchaToken }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof data.error === 'string' ? data.error : 'Magic link request failed');
    }
    // Success — email was sent (or will be, for enumeration safety)
  }, []);

  // ─── Password reset request ───────────────────────────────────

  const requestPasswordReset = useCallback(async (email: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const res = await fetch(`${apiUrl}/api/auth/password-reset/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      throw new Error('Password reset request failed');
    }
    // Always succeeds from the caller's perspective (enumeration-safe)
  }, []);

  const refreshSubscriber = useCallback(async () => {
    if (user) await fetchSubscriber(user.id);
  }, [user, fetchSubscriber]);

  const setUserTier = useCallback(async (tier: UserTier) => {
    const nextTier = normalizeUserTier(tier);
    writeStoredUserTier(nextTier);
    setUserTierState(nextTier);
    setSubscriber((current) => current ? { ...current, user_tier: nextTier } : current);

    if (!user) return;

    try {
      const { error } = await supabase
        .from('subscribers')
        .update({ user_tier: nextTier, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) {
        console.warn('[Auth] user_tier update skipped:', error.message);
      }
    } catch (err) {
      console.warn('[Auth] user_tier update error:', err);
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        subscriber,
        subscriberResolved,
        subscriberFetchFailed,
        loading,
        sessionExpired,
        signOut,
        signInWithGitHub,
        signInWithGoogle,
        signInWithPassword,
        signUpWithPassword,
        signInWithMagicLink,
        requestPasswordReset,
        refreshSubscriber,
        userTier,
        setUserTier,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

